import { useEffect, useMemo, useRef, useState } from "react";

import { Fees } from "../../../../api/api-fees";
import type { VatFeeProfile } from "../../app-main/selectors/fee-profile.selectors";
import type {
  FeeRegion,
  FeeType,
} from "../../app-main/selectors/fees.selectors";

import type { RegionalFeesResponse } from "./TaxDetails.types";
import {
  getRegistrationCountries,
  mergeRegionalFeesResponses,
} from "./TaxDetails.utils";

type RegistrationFees = {
  feeTypes: FeeType[];
  regions: FeeRegion[];
};

/** What came back, and the countries it describes. */
type LoadedRegistrationFees = {
  countriesKey: string;
  /** `null` when no country's fees could be read. */
  fees: RegistrationFees | null;
};

const EMPTY_LOADED_STATE: LoadedRegistrationFees = {
  countriesKey: "",
  fees: null,
};

/**
 * The regional fees of every country the guest holds a tax registration in, so
 * each registration is read against its own country's taxes rather than the
 * locale's. With nothing registered, `fallbackCountry`'s fees are fetched whole,
 * because the page's own are priced for one province and would hide the others'
 * taxes. `fees` is `null` with no country to ask for, or when no country's fees
 * could be read — the caller keeps the fees it already has.
 */
const useRegistrationFees = ({
  accessToken,
  fallbackCountry,
  vatProfiles,
}: {
  accessToken?: string;
  fallbackCountry?: string;
  vatProfiles: VatFeeProfile[];
}): { fees: RegistrationFees | null; isLoading: boolean } => {
  const countries = useMemo(
    () => getRegistrationCountries(vatProfiles),
    [vatProfiles],
  );
  // If user has DE and CA tax records it would be "DE,CA"
  const countriesKey = countries.length
    ? countries.join(",")
    : (fallbackCountry ?? "");
  const [loaded, setLoaded] =
    useState<LoadedRegistrationFees>(EMPTY_LOADED_STATE);

  // Names the countries the pending requests are for, so a slow answer for a
  // set of registrations that has since changed is dropped.
  const pendingCountriesKey = useRef("");

  const fetchRegionalFees = (accessToken: string, country: string) =>
    new Promise<RegionalFeesResponse>((resolve) => {
      Fees.fetchRegionalFees(accessToken, { country }, resolve);
    });

  useEffect(() => {
    pendingCountriesKey.current = countriesKey;

    if (!accessToken || !countriesKey) {
      setLoaded(EMPTY_LOADED_STATE);

      return;
    }

    Promise.all(
      countriesKey
        .split(",")
        .map((country): Promise<RegionalFeesResponse> =>
          fetchRegionalFees(accessToken, country),
        ),
    )
      .then((responses) => {
        if (countriesKey !== pendingCountriesKey.current) {
          return;
        }

        // A country that failed is left out rather than failing the rest.
        const fetched = responses.filter(
          (response) => response?.statusCode === 200,
        );
        const merged = mergeRegionalFeesResponses(fetched);

        setLoaded({
          countriesKey,
          fees: fetched.length
            ? { feeTypes: merged.fees, regions: merged.regions }
            : null,
        });
      })
      .catch(() => {
        if (countriesKey !== pendingCountriesKey.current) {
          return;
        }

        setLoaded({ countriesKey, fees: null });
      });

    // The requests cannot be canceled, so unmounting clears what they were
    // for instead — the guard above then drops whatever comes back.
    return () => {
      pendingCountriesKey.current = "";
    };
  }, [accessToken, countriesKey]);

  // Derived from which countries the held fees describe, so the render that
  // first sees a new registration does not report another country's fees as
  // loaded.
  const isLoaded = countriesKey === loaded.countriesKey;

  return {
    fees: isLoaded ? loaded.fees : null,
    isLoading: Boolean(accessToken) && !isLoaded,
  };
};

export { useRegistrationFees };
